import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Eye, CheckCircle, Clock, Bot, Copy, Sparkles, Trash2 } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { adminApi, ApiError } from '../lib/api';
import { BugReport } from '../types/titan';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';



const statusColors: Record<string, string> = {
  open: 'bg-rose-500/10 text-rose-600',
  in_progress: 'bg-amber-500/10 text-amber-600',
  resolved: 'bg-emerald-500/10 text-emerald-600',
  closed: 'bg-muted text-muted-foreground',
};

const priorityColors: Record<string, string> = {
  critical: 'bg-rose-500 text-white',
  high: 'bg-amber-500 text-white',
  medium: 'bg-blue-500/10 text-blue-600',
  low: 'bg-muted text-muted-foreground',
};

const AdminBugs: React.FC = () => {
  const [bugs, setBugs] = useState<BugReport[]>([]);
  const [selectedBug, setSelectedBug] = useState<BugReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisMsg, setAnalysisMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchBugs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.bugs({ 
        status: filter || undefined, 
        limit: 100 
      });
      setBugs(data.reports || []);
    } catch (e) {
      console.error('Bugs fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const viewBug = async (bugId: string) => {
    try {
      const bug = await adminApi.getBug(bugId);
      setSelectedBug(bug);
    } catch (e) {
      console.error('View bug failed:', e);
    }
  };

  const updateBug = async (bugId: string, updates: any) => {
    try {
      await adminApi.resolveBug(bugId, updates);
      await fetchBugs();
      if (selectedBug?.id === bugId) {
        setSelectedBug({ ...selectedBug, ...updates });
      }
    } catch (e) {
      console.error('Update bug failed:', e);
    }
  };

  const runAIAnalysis = async (bugId: string) => {
    setAnalyzing(true);
    setAnalysisMsg('Analyzing… Claude is reviewing the report (up to 60 seconds).');
    try {
      // Backend awaits the analysis synchronously — this call returns when done
      await adminApi.analyzeBug(bugId);
      // Fetch the updated bug to get ai_analysis field
      const updated = await adminApi.getBug(bugId);
      setSelectedBug(updated);
      setAnalyzing(false);
      setAnalysisMsg(null);
    } catch (e) {
      setAnalyzing(false);
      setAnalysisMsg(e instanceof ApiError ? e.detail : 'Analysis failed — check OpenRouter API key in Settings.');
    }
  };

  const deleteBug = async (bugId: string) => {
    if (!window.confirm('Delete this bug report?')) return;
    try {
      await adminApi.deleteBug(bugId);
      setSelectedBug(null);
      await fetchBugs();
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    fetchBugs();
  }, [fetchBugs]);

  const openCount = bugs.filter((b) => b.status === 'open').length;
  const criticalCount = bugs.filter(
    (b) => b.priority === 'critical' && b.status !== 'closed'
  ).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold">Bug Reports</h1>
          {openCount > 0 && (
            <Badge variant="destructive">{openCount} open</Badge>
          )}
          {criticalCount > 0 && (
            <Badge className="bg-rose-500">{criticalCount} critical</Badge>
          )}
        </div>
        <div className="flex gap-2">
          {['', 'open', 'in_progress', 'resolved', 'closed'].map((s) => (
            <Button
              key={s}
              variant={filter === s ? 'default' : 'outline'}
              size="sm"
              className="text-xs capitalize"
              onClick={() => setFilter(s)}
            >
              {s || 'All'}
            </Button>
          ))}
          <Button variant="ghost" size="icon" onClick={fetchBugs}>
            <RefreshCw
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
            />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Bug List */}
        <div className="lg:col-span-2">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reporter</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bugs.map((bug) => (
                  <TableRow
                    key={bug.id}
                    className={
                      selectedBug?.id === bug.id ? 'bg-muted/50' : ''
                    }
                  >
                    <TableCell className="font-medium text-sm max-w-[200px] truncate">
                      {bug.title}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={`text-xs ${priorityColors[bug.priority]}`}
                      >
                        {bug.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={`text-xs ${statusColors[bug.status]}`}
                      >
                        {bug.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {bug.reported_email?.split('@')[0] ||
                        bug.reported_by?.slice(0, 8)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(bug.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={() => viewBug(bug.id)}
                      >
                        <Eye className="w-3 h-3 mr-1" /> View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {bugs.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No bug reports
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </div>

        {/* Bug Detail */}
        <div>
          {selectedBug ? (
            <Card>
              <CardContent className="p-4 space-y-3">
                <h3 className="font-semibold text-sm">{selectedBug.title}</h3>
                <div className="flex gap-2">
                  <Badge className={priorityColors[selectedBug.priority]}>
                    {selectedBug.priority}
                  </Badge>
                  <Badge className={statusColors[selectedBug.status]}>
                    {selectedBug.status}
                  </Badge>
                </div>
                {selectedBug.description && (
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                    {selectedBug.description}
                  </p>
                )}
                <div className="text-xs text-muted-foreground">
                  <div>Page: {selectedBug.page}</div>
                  <div>Reporter: {selectedBug.reported_email}</div>
                  <div>
                    Date:{' '}
                    {new Date(selectedBug.created_at).toLocaleString()}
                  </div>
                </div>
                {selectedBug.screenshot && (
                  <img
                    src={selectedBug.screenshot}
                    alt="Bug screenshot"
                    className="w-full rounded border"
                  />
                )}
                {selectedBug.ai_analysis && (() => {
                  const [analysis, prompt] = selectedBug.ai_analysis.split('### Claude Code Prompt');
                  return (
                    <div className="space-y-3">
                      <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 space-y-1">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-600">
                          <Bot className="w-3.5 h-3.5" />
                          Claude AI Analysis
                          {selectedBug.ai_analyzed_at && (
                            <span className="font-normal text-muted-foreground ml-auto">
                              {new Date(selectedBug.ai_analyzed_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-foreground/80 whitespace-pre-wrap leading-relaxed">
                          {analysis.trim()}
                        </div>
                      </div>

                      {prompt && (
                        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                              <Sparkles className="w-3.5 h-3.5" />
                              Claude Code Prompt
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10"
                              onClick={() => copyToClipboard(prompt.trim())}
                              title="Copy prompt for Claude Code"
                            >
                              {copied ? (
                                <CheckCircle className="w-3 h-3 text-emerald-500" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </Button>
                          </div>
                          <div className="text-[10px] font-mono text-foreground/70 bg-black/5 dark:bg-white/5 p-2 rounded border border-emerald-500/10 max-h-[150px] overflow-auto">
                            {prompt.trim()}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* Analysis status message */}
                {analysisMsg && (
                  <div className="text-xs text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-md px-2 py-1.5 flex items-center gap-1.5">
                    <RefreshCw className="w-3 h-3 animate-spin shrink-0" />
                    {analysisMsg}
                  </div>
                )}

                {!selectedBug.ai_analysis && !analyzing && (
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground italic flex items-center gap-1.5">
                      <Bot className="w-3 h-3" />
                      AI analysis pending...
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-[10px] gap-1"
                      onClick={() => runAIAnalysis(selectedBug.id)}
                      disabled={analyzing}
                    >
                      <Sparkles className="w-3 h-3" />
                      Run AI Review
                    </Button>
                  </div>
                )}
                {selectedBug.ai_analysis && (
                  <div className="flex justify-between pt-1 items-center">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 text-[10px] text-muted-foreground gap-1"
                      onClick={() => runAIAnalysis(selectedBug.id)}
                      disabled={analyzing}
                    >
                      <RefreshCw className={`w-2.5 h-2.5 ${analyzing ? 'animate-spin' : ''}`} />
                      Re-analyze
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 text-[10px] text-rose-500 hover:text-rose-600 gap-1"
                      onClick={() => deleteBug(selectedBug.id)}
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                      Delete
                    </Button>
                  </div>
                )}
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    size="sm"
                    className="text-xs h-7"
                    variant="outline"
                    onClick={() =>
                      updateBug(selectedBug.id, { status: 'in_progress' })
                    }
                  >
                    <Clock className="w-3 h-3 mr-1" /> In Progress
                  </Button>
                  <Button
                    size="sm"
                    className="text-xs h-7 bg-emerald-500 hover:bg-emerald-600"
                    onClick={() =>
                      updateBug(selectedBug.id, { status: 'resolved' })
                    }
                  >
                    <CheckCircle className="w-3 h-3 mr-1" /> Resolve
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground text-sm">
                Select a bug to view details
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBugs;
